import * as React from 'react';
import { cn } from '@/lib/utils';
import defaultImage from '@/assets/images/placeholders/course-cover.svg';

const CourseCardAdminWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-wrapper',
      'grid gap-5',
      // Mobile: 1 coluna ocupando todo o espaço (evita o quebra de 328px em telas de 320px)
      'grid-cols-1',
      // Card virou vertical: o mínimo caiu de 328/400px para 280/320px, senão
      // cabem menos cards por linha do que cabiam no layout horizontal.
      'sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))]',
      'lg:grid-cols-[repeat(auto-fill,minmax(320px,1fr))]',
      className
    )}
    tabIndex={0}
    {...props}
  />
));
CourseCardAdminWrapper.displayName = 'CourseCardAdminWrapper';

// Card VERTICAL (imagem no topo, conteúdo embaixo). O layout anterior era
// horizontal com thumb de 144px à esquerda, o que espremia o título a ponto de
// "Fundamentos de Matemática" truncar. Vertical devolve a largura inteira do
// card ao texto. `overflow-hidden` é o que deixa a imagem sangrar até a borda
// arredondada sem sobrar canto branco.
const CourseCardAdmin = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin',
      // styles
      'group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-card transition-shadow duration-150 hover:shadow-card-hover',
      className
    )}
    {...props}
  />
));
CourseCardAdmin.displayName = 'CourseCardAdmin';

const CourseCardAdminImageWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-img-wrapper',
      // styles
      'relative aspect-[16/9] w-full flex-shrink-0 overflow-hidden bg-neutral-lightest',
      className
    )}
    {...props}
  />
));
CourseCardAdminImageWrapper.displayName = 'CourseCardAdminImageWrapper';

// Zoom no hover de 125% -> 105%: em largura cheia o 125% desloca a imagem
// inteira e fica agressivo (na thumb de 144px passava despercebido).
const CourseCardAdminImage = React.forwardRef(({ className, src, ...props }, ref) => (
  <img
    ref={ref}
    className={cn(
      'c-course-card-admin-img',
      // styles
      'h-full w-full object-cover transition-transform !duration-700 ease-in-out group-hover:scale-105 group-focus:scale-105',
      className
    )}
    src={src || defaultImage}
    {...props}
  />
));
CourseCardAdminImage.displayName = 'CourseCardAdminImage';

// Selo de estado sobre a imagem (publicado / rascunho). Aparece para todo mundo
// que vê o card, e não só para quem pode publicar — estado é informação,
// publicar é ação. Antes o estado só existia dentro do switch, então quem não
// tinha permissão de publicar não tinha como saber se o curso estava no ar.
const CourseCardAdminStatus = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-status',
      // styles
      'absolute left-3 top-3 z-[1]',
      className
    )}
    {...props}
  />
));
CourseCardAdminStatus.displayName = 'CourseCardAdminStatus';

const CourseCardAdminContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-content',
      // styles
      // Sem `md:w-36`: era largura-base de 144px num container flex-grow, e era
      // isso que apertava o título no layout antigo.
      'flex w-full max-w-full flex-grow flex-col gap-4 p-5',
      className
    )}
    {...props}
  />
));
CourseCardAdminContent.displayName = 'CourseCardAdminContent';

const CourseCardAdminHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-header',
      // styles
      'flex flex-col gap-2 [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminHeader.displayName = 'CourseCardAdminHeader';

const CourseCardAdminHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-header-content',
      // styles
      'flex items-start gap-1.5',
      className
    )}
    {...props}
  />
));
CourseCardAdminHeaderContent.displayName = 'CourseCardAdminHeaderContent';

// `line-clamp-2` no lugar de `truncate break-all`: as duas regras antigas se
// contradiziam (truncate força uma linha só; break-all quebraria no meio da
// palavra se o truncate deixasse de valer). Duas linhas cobrem os títulos reais
// sem cortar palavra.
const CourseCardAdminTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-course-card-admin-title',
      // styles
      'mb-0 line-clamp-2 text-base font-bold leading-[1.3] text-primary-dark',
      className
    )}
    {...props}
  />
));
CourseCardAdminTitle.displayName = 'CourseCardAdminTitle';

const CourseCardAdminBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-body',
      // styles
      'flex-grow [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminBody.displayName = 'CourseCardAdminBody';

// Métricas em linha, no lugar das caixas com borda do CardKpi: os rótulos têm
// larguras muito diferentes ("Estudantes" x "Módulos publicados") e as caixas
// ficavam com alturas desiguais lado a lado.
const CourseCardAdminMetrics = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-metrics',
      // styles
      'flex flex-wrap items-center gap-x-5 gap-y-2',
      className
    )}
    {...props}
  />
));
CourseCardAdminMetrics.displayName = 'CourseCardAdminMetrics';

// API por props (e não children) porque a forma é fixa: ícone + valor + rótulo.
// `onClick` é opcional — a métrica de módulos abre o modal de módulos; a de
// estudantes não faz nada, então vira <div> e não entra na navegação por teclado.
const CourseCardAdminMetric = React.forwardRef(({ className, icon: Icon, label, value, onClick, ...props }, ref) => {
  const Comp = onClick ? 'button' : 'div';

  return (
    <Comp
      ref={ref}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'c-course-card-admin-metric',
        // styles
        'flex items-center gap-1.5 text-sm',
        onClick && 'transition-colors hover:[&_span]:text-primary',
        className
      )}
      {...props}
    >
      {Icon && (
        <Icon
          size={18}
          className="flex-none text-primary"
        />
      )}
      <span className="font-bold text-primary-dark">{value}</span>
      <span className="text-[#5D6A86]">{label}</span>
    </Comp>
  );
});
CourseCardAdminMetric.displayName = 'CourseCardAdminMetric';

const CourseCardAdminFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-footer',
      // styles
      'flex items-center gap-3 [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminFooter.displayName = 'CourseCardAdminFooter';

const CourseCardAdminRating = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-card-admin-rating',
      // styles
      'mb-0 flex flex-none items-center gap-1 whitespace-nowrap text-sm font-bold text-[#5D6A86]',
      '[&_span]:text-xs [&_span]:font-light [&_span]:text-slate-500',
      '[&_svg]:min-w-[16px] [&_svg]:fill-[#FFD661] [&_svg]:stroke-[#FFD661]',
      className
    )}
    {...props}
  />
));
CourseCardAdminRating.displayName = 'CourseCardAdminRating';

export {
  // exports
  CourseCardAdminWrapper,
  CourseCardAdmin,
  CourseCardAdminImageWrapper,
  CourseCardAdminImage,
  CourseCardAdminStatus,
  CourseCardAdminContent,
  CourseCardAdminHeader,
  CourseCardAdminHeaderContent,
  CourseCardAdminTitle,
  CourseCardAdminBody,
  CourseCardAdminMetrics,
  CourseCardAdminMetric,
  CourseCardAdminFooter,
  CourseCardAdminRating
};
